import { type ComponentProps, type FormEvent, type ReactNode, useCallback, useMemo } from 'react';

import type { MaskitoOptions, MaskitoPreprocessor } from '@maskito/core';
import { maskitoInitialCalibrationPlugin } from '@maskito/core';
import { useMaskito } from '@maskito/react';

import { useFieldWiring } from '../hooks/use-field-wiring';
import { cn } from '../lib/utils';
import { Field, FieldLabel, type FieldOverrides, FieldSlot } from './field';
import {
    type Input,
    inputBaseClassName,
    inputFocusClassName,
    inputInvalidClassName,
    inputInvalidFocusClassName,
} from './input';

// `prepare` runs on every inserted chunk — one character while typing, the whole string on paste —
// and returns what the mask should consume instead.
type MaskConfig = {
    mask: string;
    prepare?: (chars: string) => string;
};

// Our public API keeps the shadcn-era `'(000) 000-0000'` pattern string; Maskito instead wants an
// array of fixed characters and per-position RegExps, so translate `0` → digit here.
function toMaskitoMask(mask: string): Array<RegExp | string> {
    return mask.split('').map((char) => (char === '0' ? /\d/ : char));
}

function toMaskitoPreprocessor(prepare: (chars: string) => string): MaskitoPreprocessor {
    return ({ elementState, data }, actionType) => ({
        elementState,
        // Mirrors react-imask's `prepare`: only the inserted chunk is rewritten, never a delete or
        // the empty-data `validation` pass `maskitoTransform` runs internally.
        data: actionType === 'insert' ? prepare(data) : data,
    });
}

// The mask holds ten digits, so a pasted number carrying its country code would otherwise have the
// leading 1 consumed as the first digit, shifting every later one: "+1 (309) 249-2342" lands as
// "(130) 924-9234". A typed "1" arrives as a one-character chunk and is left alone.
function dropUsCountryCode(chars: string): string {
    const digits = chars.replace(/\D/g, '');

    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : chars;
}

function toMaskitoOptions(config: MaskConfig): MaskitoOptions {
    return {
        mask: toMaskitoMask(config.mask),
        preprocessors: config.prepare ? [toMaskitoPreprocessor(config.prepare)] : [],
        // Matches react-imask's mount-time reformat, so a raw, unformatted seed still displays
        // masked. `maskitoUpdateElement` only dispatches `input` when the calibrated value differs
        // from what's already there, so a seed that arrives pre-formatted (as ours always do — see
        // `format-phone-for-form.ts`/`format-date-of-birth-for-form.ts`) never fires `onChange` and
        // never dirties the form.
        plugins: [maskitoInitialCalibrationPlugin()],
    };
}

const US_PHONE_MASK: MaskConfig = { mask: '(000) 000-0000', prepare: dropUsCountryCode };
const DATE_MASK: MaskConfig = { mask: '00/00/0000' };

type Props = Omit<
    ComponentProps<typeof Input>,
    'defaultValue' | 'maxLength' | 'onChange' | 'onInput' | 'type' | 'value'
> & {
    label: string;
    labelClassName?: string;
    mask: MaskConfig | string;
    value?: string;
    onChange?: (value: string) => void;
    description?: string;
    error?: boolean | ReactNode;
    fieldProps?: FieldOverrides;
    fieldClassName?: string;
};

function MaskedInputField({
    label,
    labelClassName,
    mask,
    value,
    onChange,
    description,
    error,
    id,
    fieldProps,
    fieldClassName,
    className,
    ref,
    ...inputProps
}: Props) {
    const wiring = useFieldWiring({ id, description, error });
    const { className: fieldPropsClassName, ...restFieldProps } = fieldProps ?? {};
    const maskPattern = typeof mask === 'string' ? mask : mask.mask;
    const preparePasted = typeof mask === 'string' ? undefined : mask.prepare;

    // `useMaskito` tears down and rebinds its event listeners whenever `options` changes identity —
    // depend on the pattern/prepare themselves, not a `{ mask, prepare }` object, which is a fresh
    // identity on every render when a caller passes a bare string.
    const options = useMemo(
        () => toMaskitoOptions({ mask: maskPattern, prepare: preparePasted }),
        [maskPattern, preparePasted],
    );
    const maskitoRef = useMaskito({ options });

    // Maskito needs the native element to bind to, and the caller may also forward its own `ref`
    // (react-hook-form's `Controller` does). React re-invokes a ref callback whenever its identity
    // changes, so this merge is memoised too — otherwise every render would detach and reattach the
    // input from both refs.
    const setRef = useCallback(
        (node: HTMLInputElement | null) => {
            maskitoRef(node);

            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                ref.current = node;
            }
        },
        [maskitoRef, ref],
    );

    // Maskito mutates the input's DOM value directly and signals the change via a native `input`
    // event rather than React's synthetic `onChange` — see `@maskito/react`'s own guidance.
    function handleInput(event: FormEvent<HTMLInputElement>) {
        onChange?.(event.currentTarget.value);
    }

    return (
        <Field
            data-disabled={inputProps.disabled}
            data-invalid={wiring.dataInvalid}
            className={cn(fieldPropsClassName, fieldClassName)}
            {...restFieldProps}
        >
            <FieldLabel htmlFor={wiring.controlId} className={labelClassName}>
                {label}
            </FieldLabel>
            <input
                {...inputProps}
                {...wiring.controlProps}
                data-slot="input"
                className={cn(
                    inputBaseClassName,
                    inputFocusClassName,
                    inputInvalidClassName,
                    inputInvalidFocusClassName,
                    className,
                )}
                value={value ?? ''}
                onInput={handleInput}
                ref={setRef}
            />
            <FieldSlot
                description={description}
                descriptionId={wiring.descriptionId}
                errorMessage={wiring.errorMessage}
                errorId={wiring.errorId}
            />
        </Field>
    );
}

export { DATE_MASK, type MaskConfig, MaskedInputField, US_PHONE_MASK };
